import { type BuzzLinkError, parseBuzzLinkError } from '@/api/buzzErrors'
import { useLinkBuzz } from '@/features/buzz/api'
import { BUZZ_CODE_PATTERN } from '@/features/buzz/api/models'
import { Button, Input } from '@/ui-kit'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/ui-kit/components/Dialog'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

/** Full code length: "DH" (2) + a 4-char group + a 4-char group, hyphens excluded. */
const MAX_CODE_CHARS = 10

/**
 * Uppercases, drops everything outside [A-Z0-9], caps at the full code's alphanumeric
 * length, then regroups into DH-XXXX-XXXX. Never rejects a keystroke (that is
 * WithdrawModal.tsx:466's mask, which fights paste on a hyphenated value) — it
 * transforms whatever lands in the field instead.
 */
const normalizeBuzzCode = (raw: string): string => {
  const alnum = raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, MAX_CODE_CHARS)
  return [alnum.slice(0, 2), alnum.slice(2, 6), alnum.slice(6, 10)].filter(Boolean).join('-')
}

/**
 * `dialogTrigger` renders *inside* Radix's `DialogTrigger`, which is itself a `<button>`.
 * Pass a non-interactive node styled as the CTA — never a `<Button>`, which would nest one
 * button in another. `ReferralProgramBanner.tsx:9-27` is the precedent.
 */
export const BuzzLinkForm = ({ dialogTrigger }: { dialogTrigger: ReactNode }) => {
  const { t } = useLingui()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const { mutateAsync: linkBuzz, isPending } = useLinkBuzz()

  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [linkError, setLinkError] = useState<BuzzLinkError | null>(null)
  // Every half-typed code is malformed, so the format hint stays hidden until the user has
  // signalled they are done with the field — blur, or a submit attempt (LoginPage.tsx:64 is
  // the precedent). Once revealed it tracks validity live and disappears the moment the
  // pattern matches; it never re-hides on a keystroke.
  const [isFormatHintRevealed, setIsFormatHintRevealed] = useState(false)

  useEffect(() => {
    if (!open) {
      setCode('')
      setLinkError(null)
      setIsFormatHintRevealed(false)
    }
  }, [open])

  const onCodeChange = useCallback((value: string) => {
    setCode(normalizeBuzzCode(value))
    // A rate limit survives retyping: it is time-based, so clearing it on a keystroke would
    // re-enable Submit and let the user hammer an endpoint that can only answer 429 again.
    // Closing the dialog resets it.
    setLinkError((previous) => (previous?.code === 'BUZZ_LINK_RATE_LIMITED' ? previous : null))
  }, [])

  const isRateLimited = linkError?.code === 'BUZZ_LINK_RATE_LIMITED'
  const isUnavailable = linkError?.code === 'BUZZ_UNAVAILABLE'
  const isPatternValid = BUZZ_CODE_PATTERN.test(code)
  const canSubmit = isPatternValid && !isPending && !isRateLimited

  // Only BUZZ_CODE_MALFORMED gets the Input's own inline field error — its message is a
  // format hint tied to the field's shape. Every other branch renders in the banner below.
  // The client reaches the same hint on its own: `canSubmit` and `onSubmit` both gate on the
  // pattern, so a malformed code is never sent and the server branch can never fire for one.
  // Deliberately about shape only — at this point nobody has been asked whether it is real.
  const showsLocalFormatHint = isFormatHintRevealed && !isPatternValid && code.length > 0
  const fieldErrorText =
    linkError?.code === 'BUZZ_CODE_MALFORMED' || showsLocalFormatHint ? t`Codes look like DH-XXXX-XXXX.` : undefined

  const bannerErrorText = (() => {
    if (!linkError || linkError.code === 'BUZZ_CODE_MALFORMED' || linkError.code === 'BUZZ_ALREADY_LINKED') {
      return undefined
    }
    switch (linkError.code) {
      case 'BUZZ_CODE_INVALID':
      case 'BUZZ_UNKNOWN':
        return t`That code isn't valid. Get a new one in Hive Calls.`
      case 'BUZZ_ACCOUNT_TAKEN':
        return t`That Hive Calls account is already linked to another DataHive account. The code has been used — get a new one.`
      case 'BUZZ_LINK_RATE_LIMITED':
        return linkError.retryAfterSeconds !== null
          ? t`Too many attempts. Try again in ${linkError.retryAfterSeconds} seconds.`
          : t`Too many attempts. Try again later.`
      case 'BUZZ_UNAVAILABLE':
        return t`Couldn't reach Hive Calls. Try again in a minute.`
    }
  })()

  const onSubmit = useCallback(async () => {
    if (!BUZZ_CODE_PATTERN.test(code)) {
      // Reached by Return, not by the button: Submit is disabled while the pattern fails.
      setIsFormatHintRevealed(true)
      return
    }
    if (isPending || isRateLimited) return
    setLinkError(null)
    try {
      await linkBuzz(code)
      setOpen(false)
    } catch (err) {
      const parsed = parseBuzzLinkError(err)
      setLinkError(parsed)
      switch (parsed.code) {
        case 'BUZZ_CODE_INVALID':
          // focus before select: focusing an unfocused input can collapse an existing
          // selection to the caret, which would undo the select().
          inputRef.current?.focus()
          inputRef.current?.select()
          break
        case 'BUZZ_ACCOUNT_TAKEN':
          setCode('')
          break
        case 'BUZZ_ALREADY_LINKED':
          void queryClient.invalidateQueries({ queryKey: ['buzz'] })
          setOpen(false)
          break
        default:
          break
      }
    }
  }, [code, isPending, isRateLimited, linkBuzz, queryClient])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>{dialogTrigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Link account</Trans>
          </DialogTitle>
          <DialogDescription className="mt-2">
            <Trans>Link your Hive Calls account to see what you&apos;ve earned, right here.</Trans>
          </DialogDescription>
        </DialogHeader>
        <div className="mt-1 flex flex-col gap-2">
          <Input
            ref={inputRef}
            placeholder={t`DH-XXXX-XXXX`}
            value={code}
            onChange={onCodeChange}
            onBlur={() => setIsFormatHintRevealed(true)}
            onKeyEnter={() => void onSubmit()}
            defaultPristine={false}
            isError={!!fieldErrorText}
            errorText={fieldErrorText}
          />
          <div className="text-body-3 text-gray-90 px-2">
            <Trans>
              Open Hive Calls → Profile → Link to DataHive AI → Generate code → Copy code, then paste the code here.
            </Trans>
          </div>
        </div>
        {bannerErrorText && (
          <div
            aria-live="polite"
            className="flex flex-col gap-2 rounded-lg border border-solid border-red-500/30 bg-red-500/10 px-3 py-2 text-red-500"
          >
            <span>{bannerErrorText}</span>
            {isUnavailable && (
              <Button
                variant="bordered-gray"
                size="small"
                className="self-start"
                onClick={() => void onSubmit()}
                isDisabled={isPending}
              >
                <Trans>Try again</Trans>
              </Button>
            )}
          </div>
        )}
        <DialogFooter className="mt-4 w-full gap-2">
          <Button variant="bordered-gray" className="flex-1" onClick={() => setOpen(false)} isDisabled={isPending}>
            <Trans>Cancel</Trans>
          </Button>
          <Button className="flex-1" onClick={() => void onSubmit()} isDisabled={!canSubmit} isLoading={isPending}>
            <Trans>Link</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
